import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useModuleProgress from '../hooks/useModuleProgress';
import '../styles/pages/_module-pages.scss';
import '../styles/pages/_learn-assessments.scss';
import '../styles/pages/_quiz-completion.scss';

interface QuizQuestion {
  question: string;
  options: string[];
  correctAnswer: number;
  explanation: string;
}

const quizQuestions: QuizQuestion[] = [
  {
    question: "What does integrating spiritual growth in coaching practice involve?",
    options: [
      "Only discussing religious beliefs with clients",
      "Incorporating spiritual awareness and practices into professional coaching",
      "Avoiding spiritual topics in coaching sessions",
      "Making coaching sessions religious in nature"
    ],
    correctAnswer: 1,
    explanation: "Integrating spiritual growth in coaching practice involves incorporating spiritual awareness and practices into professional coaching. This means being mindful of clients' spiritual dimensions while maintaining professional boundaries."
  },
  {
    question: "According to the module, spiritual coaching should:",
    options: [
      "Replace traditional coaching methods",
      "Complement evidence-based coaching with spiritual awareness",
      "Ignore clients' spiritual needs",
      "Focus only on spiritual development"
    ],
    correctAnswer: 1,
    explanation: "Spiritual coaching should complement evidence-based coaching with spiritual awareness. It integrates spiritual perspectives while maintaining the effectiveness of proven coaching methodologies."
  },
  {
    question: "What is the importance of maintaining professional boundaries in spiritual coaching?",
    options: [
      "It prevents any spiritual discussion",
      "It ensures client autonomy and prevents inappropriate influence",
      "It makes coaching less effective",
      "It has no practical importance"
    ],
    correctAnswer: 1,
    explanation: "Maintaining professional boundaries ensures client autonomy and prevents inappropriate influence. Spiritual coaching respects each client's unique path while providing supportive guidance."
  },
  {
    question: "The module suggests that coaches can integrate spirituality by:",
    options: [
      "Imposing their beliefs on clients",
      "Being present, listening deeply, and honoring clients' spiritual journeys",
      "Avoiding all spiritual conversations",
      "Focusing only on practical goals"
    ],
    correctAnswer: 1,
    explanation: "Coaches can integrate spirituality by being present, listening deeply, and honoring clients' spiritual journeys. This creates a safe space for clients to explore their spiritual dimensions."
  },
  {
    question: "What is the benefit of integrating spiritual growth in coaching practice?",
    options: [
      "It makes coaching less professional",
      "It provides deeper, more holistic support for client transformation",
      "It limits coaching effectiveness",
      "It has no impact on client outcomes"
    ],
    correctAnswer: 1,
    explanation: "Integrating spiritual growth provides deeper, more holistic support for client transformation. It addresses the whole person - mind, body, and spirit - leading to more comprehensive and lasting change."
  }
];

const SpiritualModule7QuizPage: React.FC = () => {
  const navigate = useNavigate();
  const { currentStep, setCurrentStep, isLoading, saveProgress } = useModuleProgress('spiritualM7');
  const [currentQuestion, setCurrentQuestion] = useState<number>(0);
  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null);
  const [showExplanation, setShowExplanation] = useState(false);
  const [responses, setResponses] = useState<{[key: number]: number}>({});
  const [showCompletion, setShowCompletion] = useState(false);

  const handleAnswerSelect = (answerIndex: number) => {
    if (responses[currentQuestion] === undefined) {
      setSelectedAnswer(answerIndex);
      setShowExplanation(true);
      setResponses(prev => ({...prev, [currentQuestion]: answerIndex}));
    }
  };

  const handleNext = () => {
    if (currentQuestion < quizQuestions.length - 1) {
      const nextQuestion = currentQuestion + 1;
      setCurrentQuestion(nextQuestion);
      setSelectedAnswer(null);
      setShowExplanation(false);
      saveProgress(nextQuestion + 1);
    } else {
      saveProgress(quizQuestions.length, true);
      setShowCompletion(true);
    }
  };

  const handleBack = () => {
    if (currentQuestion > 0) {
      const prevQuestion = currentQuestion - 1;
      const previousResponse = responses[prevQuestion];
      setCurrentQuestion(prevQuestion);
      setSelectedAnswer(previousResponse ?? null);
      setShowExplanation(previousResponse !== undefined);
      saveProgress(prevQuestion + 1);
    }
  };

  const handleExit = () => {
    navigate('/panel/learn/spiritual-resources');
  };

  if (showCompletion) {
    return (
      <div className="quiz-completion-section">
        <h2 className="completion-title">
          <span>Congratulations!</span>
        </h2>
        <p className="completion-subtitle">
          You've successfully completed the Module 7 quiz!
        </p>

        <div className="completion-card">
          <div className="achievement-section">
            <h3 className="section-title">
              <span>Key Achievements</span>
            </h3>
            <p className="achievement-intro">You've demonstrated a great understanding of:</p>
            <ul className="achievement-list">
              <li>
                <span className="check">✓</span>
                <span>Spiritual coaching integration</span>
              </li>
              <li>
                <span className="check">✓</span>
                <span>Professional boundaries</span>
              </li>
              <li>
                <span className="check">✓</span>
                <span>Holistic client support</span>
              </li>
            </ul>
          </div>

          <div className="next-steps-section">
            <h3 className="section-title">
              <span>Next Steps</span>
            </h3>
            <p>You've completed your spiritual wellness journey!</p>
            <p>Continue applying these insights in your daily life and coaching practice.</p>
          </div>

          <button
            className="resource-button"
            onClick={handleExit}
          >
            Return to Spiritual Resources
          </button>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="module-form" style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        <p>Loading your progress...</p>
      </div>
    );
  }

  const progress = ((currentQuestion + 1) / quizQuestions.length) * 100;
  const currentQuiz = quizQuestions[currentQuestion];

  return (
    <div className="module-form">
      <div className="module-container">
        <div className="module-left spiritual-panel">
          <div className="module-header">
            <h1>Module 7 Quiz</h1>
            <p className="module-subtitle">Integration – Living Your Spiritual Truth</p>
            <hr className="module-divider" />
            <p className="module-tool-info">
              Let's review what we've learned in a stress-free way.
              There's no scoring - this is just to reinforce the main ideas!
            </p>
          </div>
        </div>

        <div className="assessment-right-panel">
          <div className="assessment-content">
            <div className="module-progress">
              <div className="progress-bar">
                <div className="progress-fill" style={{ width: `${progress}%` }}></div>
              </div>
              <span className="progress-text">
                Question {currentQuestion + 1} of {quizQuestions.length}
              </span>
            </div>

            <div className="question-section">
              <h2 className="question-title">
                {currentQuiz.question}
              </h2>
              <p className="question-instruction">
                Please select the answer that best matches your understanding.
              </p>

              <div className="answer-options">
                {currentQuiz.options.map((option, index) => (
                  <label key={index} className="answer-option">
                    <input
                      type="radio"
                      name={`question-${currentQuestion}`}
                      value={index}
                      checked={selectedAnswer === index}
                      onChange={() => handleAnswerSelect(index)}
                      disabled={selectedAnswer !== null}
                    />
                    <span className="option-text">{option}</span>
                  </label>
                ))}
              </div>

              {showExplanation && (
                <div className="explanation">
                  <h3>{selectedAnswer === currentQuiz.correctAnswer ? 'Correct!' : 'Remember:'}</h3>
                  <p>{currentQuiz.explanation}</p>
                </div>
              )}
            </div>

            <div className="navigation-buttons">
              <button
                className="btn-secondary"
                onClick={currentQuestion === 0 ? handleExit : handleBack}
              >
                {currentQuestion === 0 ? 'Exit Quiz' : 'Previous Question'}
              </button>

              {currentQuestion < quizQuestions.length - 1 ? (
                <button
                  className="btn-primary"
                  onClick={handleNext}
                  disabled={selectedAnswer === null}
                >
                  Next Question
                </button>
              ) : (
                <button
                  className="btn-primary"
                  onClick={handleNext}
                  disabled={selectedAnswer === null}
                >
                  Complete Quiz
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SpiritualModule7QuizPage;

